import http from 'hub-http/clients/apiClient';
import { adapt, unadapt, getTemptStorageKey, isInterimView } from 'crm-index-visualization-core/core/utils/vizSettingsAdaptor';
import { VizSetting, normalizeVizType } from 'crm-index-visualization-core/core/model/vizSetting';
import { prepareForBackend } from '../utils/backendAdaptor';
export class ViewAPI {
  /**
   * Retrieves a view from backend and adapts it to include visualization settings
   * Loads viz settings from client cache if available, otherwise uses defaults
   * @param id - View ID to retrieve
   * @param objectTypeId - Object type ID for the view
   * @param clientStorage - Client storage instance for loading cached viz settings
   * @returns Promise resolving to adapted view with cached or default viz settings
   * @throws Error if backend request fails
   * @example ViewAPI.getView('123', 'contacts', clientStorage) // Returns view with cached settings
   */
  static async getView(id, objectTypeId, clientStorage) {
    var _ref;
    // Get view from backend
    const backendView = await http.get(`sales/v4/views/${objectTypeId}/${id}`);
    if (isInterimView(backendView)) {
      return backendView;
    }
    const vizType = normalizeVizType((_ref = backendView.currentViewVisualization) !== null && _ref !== void 0 ? _ref : 'TABLE');

    // Only fetch from client storage if we need to adapt
    const cachedSetting = await (clientStorage === null || clientStorage === void 0 ? void 0 : clientStorage.getItem(getTemptStorageKey(vizType, id, objectTypeId)).then(cachedSettingsData => {
      const parsed = JSON.parse(cachedSettingsData);
      return new VizSetting(parsed.visualizationType, parsed.settings, parsed.id);
    }).catch(() => VizSetting.default(vizType)));
    return adapt(backendView, cachedSetting !== null && cachedSetting !== void 0 ? cachedSetting : VizSetting.default(vizType));
  }

  /**
   * Updates a view in the backend and handles visualization settings caching
   * Saves FE-added viz settings to client cache and sends them to backend
   * @param view - View to update (can include FE-added viz settings)
   * @param clientStorage - Client storage instance for caching viz settings
   * @returns Promise resolving to updated view with viz settings if client storage provided
   * @throws Error if backend request fails or view missing required fields
   */
  static async putView(view, clientStorage) {
    var _view$visualizationSe;
    // go through each viz settings in the POST/PUT map payload and cache them;
    const vizSettingsMap = 'visualizationSettingsMap' in view ? (_view$visualizationSe = view.visualizationSettingsMap) !== null && _view$visualizationSe !== void 0 ? _view$visualizationSe : {} : {};
    const cacheOperations = Object.entries(vizSettingsMap).map(([vizType, vizSetting]) => {
      const cacheKey = getTemptStorageKey(vizType, view.id.toString(), view.objectTypeId);
      return clientStorage.setItem(cacheKey, JSON.stringify(vizSetting)).catch(() => {}); // ignore errors
    });

    // Strip FE-added fields before sending to backend
    const cleanView = unadapt(view);
    const backendView = prepareForBackend(cleanView);
    const [backendResponse] = await Promise.all([http.put(`sales/v4/views/${view.id}`, {
      data: backendView
    }), ...cacheOperations]);
    if (isInterimView(backendResponse)) {
      return backendResponse;
    }
    return view;
  }

  /**
   * Only use for autosaving table config when view is not hubspot defined (index page specific behavior)
   */
  static legacyPutViewDefault({
    id,
    columns,
    state,
    objectTypeId,
    idLabel
  }) {
    const idToUse = idLabel !== null && idLabel !== void 0 ? idLabel : id;
    // FIXME - Generate Sales v3 client type for PUT default-view endpoint and use it here
    return http.put(`/sales/v3/views/default-view/${objectTypeId}/${idToUse}?namespace=NONE`,
    // existing hard-coded namespace as index-page
    {
      data: {
        id: idToUse,
        columns,
        state,
        objectTypeId
      }
    });
  }

  /**
   * Creates a new view in the backend and handles visualization settings caching
   * Saves FE-added viz settings to client cache using the newly created view ID
   * @param view - View to create (can include FE-added viz settings)
   * @param clientStorage - Optional client storage instance for caching viz settings
   * @returns Promise resolving to created view with viz settings if provided
   * @throws Error if backend request fails or response missing expected fields
   */
  static async postView(view, clientStorage) {
    var _view$visualizationSe2;
    // Strip FE-added fields before sending to backend
    const cleanView = unadapt(view);
    const backendView = prepareForBackend(cleanView);
    const backendResponse = await http.post(`sales/v4/views?namespace=${view.namespaceName}`, {
      data: backendView
    });
    const vizSettingsMap = 'visualizationSettingsMap' in view ? (_view$visualizationSe2 = view.visualizationSettingsMap) !== null && _view$visualizationSe2 !== void 0 ? _view$visualizationSe2 : {} : {};
    const cacheOperations = Object.entries(vizSettingsMap).map(([vizType, vizSetting]) => {
      const cacheKey = getTemptStorageKey(vizType, backendResponse.id.toString(), backendResponse.objectTypeId);
      return clientStorage.setItem(cacheKey, JSON.stringify(vizSetting));
    });
    await Promise.all(cacheOperations).catch(() => {}); // ignore errors

    // Adapt and return the view with viz settings
    return !isInterimView(backendResponse) ? adapt(backendResponse, VizSetting.fromBEView(backendResponse, view.currentViewVisualization)) : backendResponse;
  }

  /**
   * Get a unique name for cloning a view using the backend endpoint that handles naming complexity.
   *
   * @param view - The view to clone
   * @returns The first available unique name for the cloned view
   */
  static getCloneViewName(view) {
    return http.get(`/sales/v4/views/first-available-clone-name/${encodeURIComponent(view.objectTypeId)}/${encodeURIComponent(view.name)}?namespace=${view.namespaceName}`).then(res => res.availableCloneName);
  }
}